import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, RotateCcw, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

/**
 * Onboarding-specific storage keys that can be safely cleared on crash recovery.
 */
const ONBOARDING_EPHEMERAL_KEYS = [
  'onboarding-form-state',
  'onboarding-step-progress',
];

/**
 * Clear onboarding-scoped ephemeral storage to recover from corrupted state.
 */
function clearOnboardingEphemeralState(): void {
  ONBOARDING_EPHEMERAL_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // Storage may be unavailable - ignore
    }
  });
}

/**
 * Capture non-PHI error context for diagnostics.
 */
function captureErrorContext(): Record<string, unknown> {
  return {
    timestamp: new Date().toISOString(),
    route: window.location.pathname,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    online: navigator.onLine,
    buildVersion: import.meta.env.VITE_BUILD_VERSION || 'dev',
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

interface OnboardingErrorBoundaryProps {
  children: ReactNode;
}

interface OnboardingErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Route-level Error Boundary for Onboarding.
 *
 * GUARANTEES:
 * - Never renders blank screen on error
 * - Always provides recovery actions
 * - Captures non-PHI diagnostics
 * - Provides sign-out option if onboarding is broken
 */
export class OnboardingErrorBoundary extends Component<
  OnboardingErrorBoundaryProps,
  OnboardingErrorBoundaryState
> {
  constructor(props: OnboardingErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<OnboardingErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    const context = captureErrorContext();
    console.error('[OnboardingErrorBoundary] Crash caught:', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      context,
    });
  }

  handleResetAndRetry = (): void => {
    clearOnboardingEphemeralState();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleSignOut = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore sign-out errors
    }
    window.location.href = '/login';
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-destructive/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-xl">Onboarding Error</CardTitle>
              <CardDescription>
                An error occurred during the onboarding process.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <details className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                <summary className="cursor-pointer font-medium">Technical Details</summary>
                <pre className="mt-2 whitespace-pre-wrap break-all">
                  {this.state.error?.name}: {this.state.error?.message}
                </pre>
              </details>

              <div className="text-sm text-muted-foreground">
                <p>Your onboarding progress has not been lost. You can:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Try again to continue where you left off</li>
                  <li>Sign out and try again later</li>
                  <li>Contact support if the issue persists</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button
                onClick={this.handleResetAndRetry}
                className="w-full gap-2"
                variant="default"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </Button>

              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Return to Home
              </Button>

              <Button
                onClick={this.handleSignOut}
                variant="ghost"
                className="w-full gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default OnboardingErrorBoundary;
